import React, { useEffect, useState } from 'react';
import { useIsMobile } from '../hooks/useIsMobile';
import {
    alreadyInstalled,
    dismissInstallPrompt,
    isIosSafari,
    isStandalone,
    promptInstall,
    queryInstalled,
    rememberInstalled,
    subscribeInstallState,
    wasInstallDismissed,
} from './pwaInstall';
import './MobileLayout.css';

export default function InstallPrompt() {
    const isMobile = useIsMobile();
    const [helpOpen, setHelpOpen] = useState(false);
    const [installed, setInstalled] = useState(alreadyInstalled);
    const [dismissed, setDismissed] = useState(wasInstallDismissed);

    useEffect(() => {
        let cancelled = false;

        const applyInstalled = (installedNow) => {
            if (cancelled) return;
            setInstalled(installedNow);
            if (!installedNow) setDismissed(wasInstallDismissed());
        };

        const markInstalled = () => {
            rememberInstalled();
            applyInstalled(true);
        };

        if (isStandalone()) markInstalled();
        window.addEventListener('appinstalled', markInstalled);
        const unsubscribe = subscribeInstallState(applyInstalled);

        const check = () => {
            queryInstalled().then(applyInstalled);
        };
        check();

        const onVisible = () => {
            if (document.visibilityState === 'visible') check();
        };
        document.addEventListener('visibilitychange', onVisible);
        window.addEventListener('focus', check);

        return () => {
            cancelled = true;
            unsubscribe();
            window.removeEventListener('appinstalled', markInstalled);
            document.removeEventListener('visibilitychange', onVisible);
            window.removeEventListener('focus', check);
        };
    }, []);

    if (dismissed || installed) {
        return null;
    }

    const dismiss = () => {
        setDismissed(true);
        setHelpOpen(false);
        dismissInstallPrompt();
    };

    const install = async () => {
        const choice = await promptInstall();
        if (!choice) {
            setHelpOpen(true);
            return;
        }
        if (choice.outcome === 'accepted') {
            rememberInstalled();
            setInstalled(true);
        }
    };

    const help = isIosSafari()
        ? 'En el iPhone no aparece un cartel solo. Tocá Compartir en Safari y elegí “Agregar a inicio”.'
        : isMobile
            ? 'En Android, abrí el menú de Chrome y elegí “Instalar app”.'
            : 'En Chrome o Edge, abrí el menú (tres puntos) y elegí “Instalar Marketplace”. También podés usar el ícono de instalación en la barra de direcciones. Después se abre en su propia ventana.';

    return (
        <div className="pwa-install" role="dialog" aria-label="Instalar Marketplace">
            <p className="pwa-install__text">
                {helpOpen
                    ? help
                    : isMobile
                        ? 'Instalá Marketplace en el celular y abrilo como una aplicación.'
                        : 'Instalá Marketplace en esta computadora y abrilo en su propia ventana.'}
            </p>
            <button type="button" className="pwa-install__action" onClick={install}>
                Instalar
            </button>
            <button type="button" className="pwa-install__dismiss" onClick={dismiss}>
                Ahora no
            </button>
        </div>
    );
}
